/**
 * The shared UI kit: buttons, form controls, cards, banners, badges, the
 * confirm dialog and an accessible tab list. Every class here names a design
 * token (styles/globals.css), never a fixed color, so a tenant theme reaches
 * all of it. Pages compose these instead of carrying their own class strings.
 */

import { useEffect, useRef, useState, type ComponentProps, type ElementType, type KeyboardEvent, type ReactNode } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useVisibleArea } from "@/lib/visible-area";

export function cn(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

const focusRing =
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background";

// ─── Button ────────────────────────────────────────────────────────────

const BUTTON_VARIANTS = {
  primary: "bg-primary text-primary-foreground hover:bg-primary/90 font-medium",
  secondary: "border border-input bg-card text-foreground hover:bg-muted",
  danger: "bg-destructive text-destructive-foreground hover:bg-destructive/90 font-medium",
  ghost: "text-foreground hover:bg-muted",
} as const;

export type ButtonVariant = keyof typeof BUTTON_VARIANTS;

export function buttonClass(variant: ButtonVariant = "primary", className?: string): string {
  return cn(
    "inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition disabled:cursor-not-allowed disabled:opacity-50",
    focusRing,
    BUTTON_VARIANTS[variant],
    className,
  );
}

export function Button({
  variant = "primary",
  type = "button",
  className,
  ...props
}: ComponentProps<"button"> & { variant?: ButtonVariant }) {
  return <button type={type} className={buttonClass(variant, className)} {...props} />;
}

export const linkClass = `rounded-sm font-medium underline underline-offset-2 hover:no-underline ${focusRing}`;

/** A button that reads as a link, for an action inside a sentence. */
export function LinkButton({ type = "button", className, ...props }: ComponentProps<"button">) {
  return <button type={type} className={cn(linkClass, className)} {...props} />;
}

// ─── Form controls ─────────────────────────────────────────────────────

const control =
  "mt-1 block w-full rounded-md border border-input bg-card px-3 py-2 text-sm font-normal text-foreground placeholder:text-muted-foreground/70 focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring disabled:opacity-60";

export function Input({ className, ...props }: ComponentProps<"input">) {
  return <input className={cn(control, className)} {...props} />;
}

export function Select({ className, ...props }: ComponentProps<"select">) {
  return <select className={cn(control, className)} {...props} />;
}

export function Textarea({ className, ...props }: ComponentProps<"textarea">) {
  return <textarea className={cn(control, className)} {...props} />;
}

/** A label around its control: the name, an optional aside, the control, and help beneath. */
export function Field({
  label,
  aside,
  help,
  className,
  children,
}: {
  label: ReactNode;
  /** A quiet note after the name, e.g. "(optional)". */
  aside?: ReactNode;
  help?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  return (
    <label className={cn("block text-sm font-medium text-foreground", className)}>
      {label}
      {aside && <span className="font-normal text-muted-foreground"> {aside}</span>}
      {children}
      {help && <span className="mt-1 block text-xs font-normal text-muted-foreground">{help}</span>}
    </label>
  );
}

// ─── Card ──────────────────────────────────────────────────────────────

export function Card<T extends ElementType = "div">({
  as,
  flush = false,
  className,
  ...props
}: {
  as?: T;
  /** No padding of its own, for content that runs to the card's edge. */
  flush?: boolean;
  className?: string;
} & Omit<ComponentProps<T>, "as" | "className">) {
  const Tag: ElementType = as ?? "div";
  return (
    <Tag
      className={cn("rounded-lg border border-border bg-card text-card-foreground", !flush && "p-4 sm:p-5", className)}
      {...props}
    />
  );
}

// ─── Banner ────────────────────────────────────────────────────────────

const TONES = {
  neutral: "border-border bg-card text-card-foreground",
  info: "border-info-soft-foreground/20 bg-info-soft text-info-soft-foreground",
  success: "border-success-soft-foreground/20 bg-success-soft text-success-soft-foreground",
  warning: "border-warning-soft-foreground/20 bg-warning-soft text-warning-soft-foreground",
  danger: "border-destructive-soft-foreground/20 bg-destructive-soft text-destructive-soft-foreground",
} as const;

export type Tone = keyof typeof TONES;

/** A message across the top of a screen or inside a card. `danger` is announced as an alert. */
export function Banner({
  tone = "neutral",
  role = tone === "danger" ? "alert" : undefined,
  className,
  children,
}: {
  tone?: Tone;
  role?: "alert" | "status";
  className?: string;
  children: ReactNode;
}) {
  return (
    <div role={role} className={cn("rounded-lg border px-4 py-3 text-sm", TONES[tone], className)}>
      {children}
    </div>
  );
}

// ─── Load failed ───────────────────────────────────────────────────────

/** What a screen or a card shows in place of a spinner when its first load failed. */
export function LoadFailed({ error, onRetry, className }: { error: string; onRetry: () => void; className?: string }) {
  return (
    <Banner tone="danger" className={className}>
      {error} <LinkButton onClick={onRetry}>Try again</LinkButton>
    </Banner>
  );
}

// ─── Empty state ───────────────────────────────────────────────────────

/** What a list shows when there is nothing in it yet, with the way forward. */
export function EmptyState({
  title,
  children,
  action,
  className,
}: {
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn("rounded-lg border border-dashed border-input bg-card px-4 py-6 text-center text-sm sm:px-6", className)}>
      {title && <p className="font-semibold text-foreground">{title}</p>}
      {children && <p className={cn("text-muted-foreground", title ? "mt-1" : undefined)}>{children}</p>}
      {action && <div className="mt-4 flex flex-wrap justify-center gap-2">{action}</div>}
    </div>
  );
}

// ─── Page header ───────────────────────────────────────────────────────

export function PageHeader({ title, description, children }: { title: string; description?: ReactNode; children?: ReactNode }) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3">
      <div className="min-w-0">
        <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
        {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
      </div>
      {children}
    </header>
  );
}

// ─── Spinner ───────────────────────────────────────────────────────────

export function Spinner({ label = "Loading", className = "py-20" }: { label?: string; className?: string }) {
  return (
    <div role="status" aria-label={label} className={`flex items-center justify-center ${className}`}>
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-border border-b-primary" aria-hidden="true" />
    </div>
  );
}

// ─── Working ───────────────────────────────────────────────────────────

/**
 * For something that takes long enough to wonder whether it is still going:
 * says what is happening and for how long it has been.
 */
export function Working({ children, className }: { children: ReactNode; className?: string }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setSeconds((s) => s + 1), 1000);
    return () => clearInterval(timer);
  }, []);
  return (
    <div role="status" className={cn("flex items-start gap-3 rounded-lg border border-border bg-muted px-4 py-3 text-sm", className)}>
      <span aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-border border-b-primary" />
      <span>
        {children}
        {/* Not announced every second; it is there to look at. */}
        <span aria-hidden="true" className="ml-1 tabular-nums text-muted-foreground">
          {seconds}s
        </span>
      </span>
    </div>
  );
}

// ─── Badges ────────────────────────────────────────────────────────────

export function Badge({ tone = "neutral", className, children }: { tone?: Tone; className?: string; children: ReactNode }) {
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium", TONES[tone], className)}>
      {children}
    </span>
  );
}

const STATUS_TONES: Record<string, Tone> = {
  active: "success",
  draft: "warning",
  running: "info",
  completed: "success",
  failed: "danger",
};

/** What a status reads as: words a person would use, never the stored value. */
const STATUS_WORDS: Record<string, string> = {
  active: "Connected",
  paused: "Paused",
  draft: "Draft",
  running: "Running",
  completed: "Done",
  failed: "Failed",
  disconnected: "Disconnected",
};

export function StatusBadge({ status, label }: { status: string; label?: string }) {
  const words = label ?? STATUS_WORDS[status] ?? status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
  return <Badge tone={STATUS_TONES[status] ?? "neutral"}>{words}</Badge>;
}

// ─── Confirm dialog ────────────────────────────────────────────────────

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  children,
  confirmLabel,
  tone = "default",
  busy = false,
  onConfirm,
  extra,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  children?: ReactNode;
  confirmLabel: string;
  tone?: "default" | "danger";
  busy?: boolean;
  onConfirm: () => void;
  /** A further action beside Cancel, e.g. a test send before approving. */
  extra?: ReactNode;
}) {
  // Embedded, the frame is as tall as its content and the host page scrolls: `top-24`
  // would sit at the top of the frame, wherever that is. Place it in the visible part.
  const visible = useVisibleArea();
  const placed = visible ? { top: Math.round(visible.top + Math.min(96, visible.height * 0.15)) } : undefined;
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/40" />
        <Dialog.Content
          style={placed}
          className={cn(
            "fixed left-1/2 z-50 w-[calc(100%-2rem)] max-w-md -translate-x-1/2 rounded-lg border border-border bg-popover p-5 text-popover-foreground shadow-xl focus:outline-none",
            !placed && "top-24",
          )}
        >
          <Dialog.Title className="text-base font-semibold">{title}</Dialog.Title>
          {children && (
            <Dialog.Description asChild>
              <div className="mt-2 text-sm text-muted-foreground">{children}</div>
            </Dialog.Description>
          )}
          <div className="mt-5 flex flex-wrap justify-end gap-2">
            {extra && <div className="mr-auto">{extra}</div>}
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button variant={tone === "danger" ? "danger" : "primary"} disabled={busy} onClick={onConfirm}>
              {confirmLabel}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

// ─── Tabs (WAI-ARIA tablist with arrow-key navigation) ─────────────────

export interface TabDef<K extends string> {
  key: K;
  label: string;
  icon?: ReactNode;
  count?: number;
}

export function TabList<K extends string>({
  tabs,
  active,
  onChange,
  idPrefix,
  label,
}: {
  tabs: TabDef<K>[];
  active: K;
  onChange: (key: K) => void;
  idPrefix: string;
  label: string;
}) {
  const refs = useRef<Record<string, HTMLButtonElement | null>>({});

  function onKeyDown(e: KeyboardEvent<HTMLButtonElement>, index: number) {
    let next = -1;
    if (e.key === "ArrowRight") next = (index + 1) % tabs.length;
    else if (e.key === "ArrowLeft") next = (index - 1 + tabs.length) % tabs.length;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = tabs.length - 1;
    if (next < 0) return;
    e.preventDefault();
    onChange(tabs[next].key);
    refs.current[tabs[next].key]?.focus();
  }

  return (
    <div role="tablist" aria-label={label} className="flex gap-0 -mb-px overflow-x-auto">
      {tabs.map((tab, i) => {
        const selected = tab.key === active;
        return (
          <button
            key={tab.key}
            ref={(el) => {
              refs.current[tab.key] = el;
            }}
            role="tab"
            id={`${idPrefix}-tab-${tab.key}`}
            aria-selected={selected}
            aria-controls={`${idPrefix}-panel-${tab.key}`}
            tabIndex={selected ? 0 : -1}
            onClick={() => onChange(tab.key)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={`inline-flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring ${
              selected ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="ml-1 px-1.5 py-0.5 rounded-full text-xs bg-muted text-muted-foreground">
                <span className="sr-only">(</span>
                {tab.count}
                <span className="sr-only">)</span>
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export function TabPanel({
  idPrefix,
  tabKey,
  children,
}: {
  idPrefix: string;
  tabKey: string;
  children: ReactNode;
}) {
  return (
    <div
      role="tabpanel"
      id={`${idPrefix}-panel-${tabKey}`}
      aria-labelledby={`${idPrefix}-tab-${tabKey}`}
      tabIndex={0}
      className="rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:ring-offset-background"
    >
      {children}
    </div>
  );
}

// ─── Misc ──────────────────────────────────────────────────────────────

export function formatWhen(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}
